import { useState, useEffect, useCallback } from 'react';
import { accountingApi } from '../api';
import type { AccountingSummary, UserAccountingDetail, PlayerAccountingSummary, PlayerManagerDetail } from '../types';

// =====================
// Manager View Hooks
// =====================

/**
 * Hook for fetching the accounting summary across all completed boards (manager view)
 */
export function useAccountingSummary() {
  const [summary, setSummary] = useState<AccountingSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSummary = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await accountingApi.getSummary();
      setSummary(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch accounting summary');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  return {
    summary,
    isLoading,
    error,
    refresh: fetchSummary,
  };
}

/**
 * Hook for fetching detailed accounting for a specific user or offline player (manager view)
 */
export function useAccountingDetail(type: 'user' | 'offline', id: string) {
  const [detail, setDetail] = useState<UserAccountingDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDetail = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = type === 'user'
        ? await accountingApi.getUserDetail(id)
        : await accountingApi.getOfflinePlayerDetail(id);
      setDetail(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch accounting detail');
    } finally {
      setIsLoading(false);
    }
  }, [type, id]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  return {
    detail,
    isLoading,
    error,
    refresh: fetchDetail,
  };
}

// =====================
// Player View Hooks (self-view)
// =====================

/**
 * Hook for fetching the current user's balances across all managers (player view)
 */
export function useMyAccountingBalances() {
  const [summary, setSummary] = useState<PlayerAccountingSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchBalances = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await accountingApi.getMyBalances();
      setSummary(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch your balances');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBalances();
  }, [fetchBalances]);

  return {
    summary,
    isLoading,
    error,
    refresh: fetchBalances,
  };
}

/**
 * Hook for fetching detailed accounting with a specific manager (player view)
 */
export function useMyManagerDetail(managerId: string) {
  const [detail, setDetail] = useState<PlayerManagerDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDetail = useCallback(async () => {
    if (!managerId) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await accountingApi.getMyManagerDetail(managerId);
      setDetail(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch manager detail');
    } finally {
      setIsLoading(false);
    }
  }, [managerId]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  return {
    detail,
    isLoading,
    error,
    refresh: fetchDetail,
  };
}
